// Web port of teamplayer/app/view-routes/_utils/routeUtils.ts
// Coordinates use { lat, lng } keys so values drop straight into
// @react-google-maps/api <Marker position> / <Polyline path> props.

/**
 * Extract { lat, lng } from the various point shapes the location-history
 * endpoint returns (raw history docs, trip wrappers with a `latlng` array, etc).
 * Returns null when no valid coordinate can be found.
 */
export const extractLatLng = (value) => {
  if (!value) return null;

  const lat = parseFloat(
    String(
      value.latitude ??
        value.lat ??
        value?.location_info?.latitude ??
        value?.location_info?.lat ??
        value?.item?.location_info?.latitude ??
        value?.item?.location_info?.lat ??
        value?.location?.latitude ??
        value?.location?.lat ??
        value?.coords?.latitude ??
        value?.coords?.lat ??
        (Array.isArray(value.latlng) ? value.latlng[0] : undefined)
    )
  );

  const lng = parseFloat(
    String(
      value.longitude ??
        value.lng ??
        value?.location_info?.longitude ??
        value?.location_info?.lng ??
        value?.item?.location_info?.longitude ??
        value?.item?.location_info?.lng ??
        value?.location?.longitude ??
        value?.location?.lng ??
        value?.coords?.longitude ??
        value?.coords?.lng ??
        (Array.isArray(value.latlng) ? value.latlng[1] : undefined)
    )
  );

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

  return { lat, lng };
};

/**
 * Extract { lat, lng, timestamp } (timestamp in ms) for playback.
 * Returns null when coordinates or a usable timestamp are missing.
 */
export const extractTimedLatLng = (value) => {
  const coords = extractLatLng(value);
  if (!coords) return null;

  const ts =
    value?.time_at_location ??
    value?.createdAt ??
    value?.timestamp ??
    value?.time ??
    value?.item?.time_at_location ??
    value?.item?.createdAt;

  const t = ts ? new Date(ts).getTime() : NaN;
  if (!Number.isFinite(t)) return null;

  return { ...coords, timestamp: t };
};

/**
 * Flatten the endpoint's `data` payload (an array that may contain nested
 * arrays and/or bare point objects) into a sorted, de-duplicated list of
 * timestamped points suitable for useRoutePlayback.
 */
export const buildRoutePoints = (travelHistory) => {
  if (!Array.isArray(travelHistory)) return [];

  const flat = [];
  travelHistory.forEach((entry) => {
    if (Array.isArray(entry)) flat.push(...entry);
    else if (entry) flat.push(entry);
  });

  const points = flat
    .map((entry) => extractTimedLatLng(entry))
    .filter(Boolean)
    .sort((a, b) => a.timestamp - b.timestamp);

  // Drop consecutive points sharing an identical timestamp (interpolation
  // divides by the timestamp delta).
  const deduped = [];
  points.forEach((p) => {
    const prev = deduped[deduped.length - 1];
    if (prev && prev.timestamp === p.timestamp) return;
    deduped.push(p);
  });

  return deduped;
};
